/**
 * Admin Routes Configuration
 * 
 * Permission System:
 * 
 * Routes can have flexible permission requirements:
 * 
 * 1. Single Permission (string):
 *    meta: { permission: 'admin_order_crud' }
 *    - User must have this specific permission
 * 
 * 2. Multiple Permissions with ANY match (array, default):
 *    meta: { 
 *      permission: ['admin_order_crud', 'admin_payment_crud'],
 *      permissionMatch: 'any' // optional, 'any' is default
 *    }
 *    - User must have AT LEAST ONE of these permissions
 * 
 * 3. Multiple Permissions with ALL match (array):
 *    meta: { 
 *      permission: ['admin_order_crud', 'admin_payment_crud'],
 *      permissionMatch: 'all'
 *    }
 *    - User must have ALL of these permissions
 * 
 * 4. No Permission Required:
 *    meta: { permission: null }
 *    - Route is accessible to all authenticated users
 */

// Define a helper for getting restaurant context titles
const getRestoTitle = (route, pageName) => {
  // This function will be executed when the route is accessed
  // The restaurant name will be provided by the store in AppHeader.vue
  // Returning just the pageName allows for cleaner breadcrumbs and titles
  return pageName
}

export let admin_routes = [
  //START: admin views
  {
    path: '/admin/account/login',
    name: 'adminLogin',
    component: () => import('@/views/admin/account/AdminLoginView.vue'),
    meta: { title: 'Admin Login' }
  },
  {
    path: '/admin/home',
    name: 'adminDashboard',
    component: () => import('@/views/admin/AdminDashboardView.vue'),
    meta: { requiresAdminAuth: true, title: 'Admin Dashboard' }
  },
  {
    path: '/admin/account/logout',
    name: 'adminLogout',
    component: () => import('@/views/admin/account/AdminLogoutView.vue'),
    meta: { title: 'Admin Logout' }
  },
  {
    path: '/admin/account/signup',
    name: 'adminSignup',
    component: () => import('@/views/admin/account/AdminSignUpView.vue'),
    meta: { title: 'Admin Signup' }
  },
  {
    path: '/admin/account/forgot_password',
    name: 'adminForgotPassword',
    component: () => import('@/views/admin/account/AdminForgotPasswordView.vue'),
    meta: { title: 'Admin Forgot Password' }
  },
  {
    path: '/admin/account/reset/:token',
    name: 'adminResetPassword',
    component: () => import('@/views/admin/account/AdminResetPasswordView.vue'),
    meta: { title: 'Admin Reset Password' }
  },
  {
    // Redirect old website reset password link to new vue website reset password link
    path: '/admin/reset/:token',
    name: 'adminResetPasswordOld',
    meta: { title: 'Admin Reset Password' },
    redirect: (to) => {
      return { name: 'clientResetPassword', params: { token: to.params.token } }
    }
  },
  {
    path: '/admin/account/setting',
    name: 'adminSetting',
    component: () => import('@/views/admin/account/setting/AdminSettingView.vue'),
    meta: { requiresAdminAuth: true, title: 'Admin Setting' }
  },
  {
    path: '/admin/account/update_email',
    name: 'adminUpdateEmail',
    component: () => import('@/views/admin/account/setting/AdminUpdateEmailView.vue'),
    meta: { requiresAdminAuth: true, title: 'Admin Update Email' }
  },
  {
    path: '/admin/account/update_password',
    name: 'adminUpdatePassword',
    component: () => import('@/views/admin/account/setting/AdminUpdatePasswordView.vue'),
    meta: { requiresAdminAuth: true, title: 'Admin Update password' }
  },
  {
    path: '/admin/client/list',
    name: 'adminClientList',
    component: () => import('@/views/admin/client/ClientListView.vue'),
    meta: { requiresAdminAuth: true, title: 'Client List' }
  },
  {
    path: '/admin/client/:client_id',
    name: 'adminClientDetail',
    component: () => import('@/views/admin/client/ClientDetailView.vue'),
    meta: { requiresAdminAuth: true, title: 'Client Detail' }
  },
  {
    path: '/admin/resto/list',
    name: 'adminRestoList',
    component: () => import('@/views/admin/RestoListView.vue'),
    meta: { requiresAdminAuth: true, title: 'Restaurant List' }
  },
  {
    path: '/admin/resto/:resto_id/home',
    name: 'adminRestoHome',
    component: () => import('@/views/admin/RestoHomeView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Dashboard'),
      permission: null // Always accessible
    }
  },
  {
    path: '/admin/resto/:resto_id/reservations',
    name: 'adminReservationList',
    component: () => import('@/views/admin/ReservationListView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Reservations'),
      permission: 'admin_reservation_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/orders',
    name: 'orderList',
    component: () => import('@/views/admin/order/OrderListView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Orders'),
      permission: 'admin_order_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/order/:order_id',
    name: 'orderDetail',
    component: () => import('@/views/admin/order/OrderDetailView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, `Order #${route.params.order_id}`),
      permission: 'admin_order_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/menu/edit',
    name: 'editMenu',
    component: () => import('@/views/admin/menu/EditMenuView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Edit Menu'),
      permission: 'admin_menuedit_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/epos_orders',
    name: 'eposOrderList',
    component: () => import('@/views/admin/order/EposOrderListView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'EPOS Orders'),
      permission: 'admin_order_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/epos_order/:order_id',
    name: 'eposOrderDetail',
    component: () => import('@/views/admin/order/EposOrderDetailView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, `EPOS Order #${route.params.order_id}`),
      permission: 'admin_order_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/resto/edit',
    name: 'editResto',
    component: () => import('@/views/admin/resto/EditRestoView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Edit Restaurant'),
      permission: 'admin_setting_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/payments',
    name: 'payments',
    component: () => import('@/views/admin/payment/PaymentListView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Payments'),
      permission: 'admin_payment_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/payment/:payment_id',
    name: 'paymentDetail',
    component: () => import('@/views/admin/payment/PaymentDetailView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, `Payment #${route.params.payment_id}`),
      permission: 'admin_payment_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/delivery_setting',
    name: 'deliverySetting',
    component: () => import('@/views/admin/DeliverySettingView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Delivery Settings'),
      permission: 'admin_collection_delivery_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/marketing',
    name: 'marketing',
    component: () => import('@/views/admin/resto/MarketingView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Marketing'),
      permission: 'admin_marketing_crud'
    }
  },
  {
    path: '/admin/resto/:resto_id/opening_times',
    name: 'openingTimes',
    component: () => import('@/views/admin/resto/OpeningTimesView.vue'),
    meta: { 
      requiresAdminAuth: true,
      title: route => getRestoTitle(route, 'Opening Times'),
      permission: 'admin_opening_times_crud'
    }
  }
  // Example routes with multiple permissions (commented out):
  // 
  // Route accessible with ANY of the listed permissions:
  // {
  //   path: '/admin/resto/:resto_id/reports',
  //   name: 'reports',
  //   component: () => import('@/views/admin/ReportsView.vue'),
  //   meta: {
  //     requiresAdminAuth: true,
  //     title: 'Reports',
  //     permission: ['admin_order_crud', 'admin_payment_crud', 'admin_reservation_crud'],
  //     permissionMatch: 'any' // User needs ANY one of these permissions
  //   }
  // }
  //
  // Route accessible only with ALL listed permissions:
  // {
  //   path: '/admin/resto/:resto_id/advanced_settings',
  //   name: 'advancedSettings',
  //   component: () => import('@/views/admin/AdvancedSettingsView.vue'),
  //   meta: {
  //     requiresAdminAuth: true,
  //     title: 'Advanced Settings',
  //     permission: ['admin_setting_crud', 'admin_restoconfig_crud'],
  //     permissionMatch: 'all' // User needs ALL of these permissions
  //   }
  // }
]
